/**
 * Resolves how a clicked link inside an AI-agent chat message should be handled.
 *
 * The agent emits three kinds of links:
 * - AI-generated file downloads (`/uploads/ai-files/...`) → save-as
 * - Profile / in-app links, now emitted as absolute Confirm URLs → SPA navigation
 *   (we strip the host when it matches the current origin so we don't open a new
 *   tab / trigger a full reload)
 * - Everything else → open in a new tab
 */
export type ChatLinkAction =
  | { kind: 'download'; filename: string; url: string }
  | { kind: 'internal'; path: string }
  | { kind: 'external'; url: string };

export function resolveChatLinkAction(
  href: string,
  currentOrigin: string
): ChatLinkAction {
  if (href.includes('/uploads/ai-files/')) {
    const filename = href.split('/').pop() || 'download';
    return { kind: 'download', filename, url: href };
  }

  let relativeHref = href;
  if (!relativeHref.startsWith('/')) {
    try {
      const url = new URL(href);
      if (url.origin === currentOrigin) {
        relativeHref = `${url.pathname}${url.search}${url.hash}`;
      }
    } catch {
      // Not a parseable absolute URL — treat as external below.
    }
  }

  if (!relativeHref.startsWith('/')) {
    return { kind: 'external', url: href };
  }
  return { kind: 'internal', path: relativeHref };
}
